import { zodResolver } from '@hookform/resolvers/zod';
import { TextareaAutosize } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useCurrentUser, useFeedbackReviewMutation } from 'src/hooks';
import type { TReviewFormState } from 'src/types';

export type SadFeedbackForm = {
  feedback: string;
  email?: string;
};

type Props = {
  setReviewFormState: React.Dispatch<React.SetStateAction<TReviewFormState>>;
  cartId: string;
};

export const FeedbackModalSad = ({ setReviewFormState, cartId }: Props) => {
  const mutation = useFeedbackReviewMutation(setReviewFormState, cartId);
  const { user } = useCurrentUser();

  const { t } = useTranslation();

  const schema = z.object({
    feedback: z
      .string()
      .min(4, { message: t('get.please_let_us_know_how_we_can_do_better') })
      .max(2000, {
        message: 'Please keep your feedback under 2000 characters.',
      }),
    // Optional email: empty string from the input is coerced to undefined so it's not validated as email
    email: z
      .union([
        z.string().email({ message: t('common.please_enter_a_valid_email_address') }),
        z.literal(''),
      ])
      .optional()
      .transform((val) => (val === '' ? undefined : val)),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      feedback: '',
      email: user?.email ?? '',
    },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    mutation.mutate({ reaction: 'unhappy', data });
  };

  return (
    <div className='flex w-full max-w-[400px] flex-col items-center justify-center gap-4'>
      <img
        src='https://assets.share-a-cart.com/images/next/cena-shock.gif'
        width='250px'
        height='139.73px'
        alt='John Cena Shocked GIF'
        className='rounded-[32px]'
      />
      <h3 className='text-center font-primary text-2xl font-bold text-primary-dark'>
        {t('get.no_how_can_we_improve')}
      </h3>
      <div className='flex w-full flex-col items-center justify-center gap-6'>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='flex w-full max-w-[360px] flex-col items-center justify-center gap-4'
        >
          <div className='flex w-full flex-col items-start justify-center gap-0.5'>
            <TextareaAutosize
              {...register('feedback')}
              autoComplete='off'
              placeholder={t('get.say_as_much_or_as_little_as_you_like')}
              minRows={6}
              maxLength={2000}
              className='w-full resize-none rounded border-none bg-offWhite px-3 py-2 font-secondary font-semibold leading-[24px] text-primary-dark outline-none ring-2 ring-gray-300 transition-all selection:bg-primary selection:text-offWhite placeholder:font-normal placeholder:text-[#636E7A] hover:bg-gray-200 focus:ring-2 focus:ring-primary hover:focus:bg-offWhite'
              name='feedback'
            />
            <span className='text-red-500'>{errors.feedback?.message?.toString()}</span>
          </div>

          <div className='flex w-full flex-col items-center justify-start gap-2'>
            <div className='flex w-full flex-col items-center justify-start gap-0.5'>
              <input
                {...register('email')}
                placeholder={t('about.your_email_address')}
                className='h-12 w-full rounded border-none bg-offWhite px-3 py-2 font-secondary font-semibold leading-[24px] text-primary-dark outline-none ring-2 ring-gray-300 transition-all selection:bg-primary selection:text-offWhite placeholder:font-normal placeholder:text-[#636E7A] hover:bg-gray-200 focus:ring-2 focus:ring-primary hover:focus:bg-offWhite'
              />
              <span className='w-full text-left text-red-500'>
                {errors.email?.message?.toString()}
              </span>
            </div>
          </div>

          <button
            type='submit'
            disabled={mutation.isPending}
            className='button-primary h-12 w-full text-wrap text-center'
          >
            {t('get.submit_feedback')}
          </button>
        </form>
      </div>
    </div>
  );
};
